/**
 * <event-add-to-calendar> — adds an event to the visitor's calendar.
 * "ics" builds an iCalendar file (opens the native calendar on iOS/Android,
 * Outlook, Apple Calendar); "google" opens a prefilled Google Calendar link.
 * Times are floating local times (Europe/Zurich event, Swiss audience).
 */
class EventAddToCalendar extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('.evt-cal-toggle');
    this.menu = this.querySelector('.evt-cal-menu');
    this.toggle.addEventListener('click', () => this.setOpen(this.menu.hidden));
    this.querySelectorAll('.evt-cal-option').forEach((btn) =>
      btn.addEventListener('click', () => {
        if (btn.dataset.cal === 'google') window.open(this.googleUrl(), '_blank', 'noopener');
        else this.downloadIcs();
        this.setOpen(false);
      })
    );
  }

  setOpen(open) {
    this.menu.hidden = !open;
    this.toggle.setAttribute('aria-expanded', String(open));
  }

  get slots() {
    const dates = (this.dataset.dates || '').split('|').filter(Boolean);
    const starts = (this.dataset.starts || '').split('|');
    const ends = (this.dataset.ends || '').split('|');
    return dates.map((date, i) => ({ date, start: this.parseTime(starts[i]), end: this.parseTime(ends[i]) }));
  }

  parseTime(value) {
    const m = /(\d{1,2})[:.](\d{2})/.exec(value || '');
    return m ? [Number(m[1]), Number(m[2])] : null;
  }

  pad(n) {
    return String(n).padStart(2, '0');
  }

  stamp(date, time) {
    const [y, mo, d] = date.split('-');
    return time ? `${y}${mo}${d}T${this.pad(time[0])}${this.pad(time[1])}00` : `${y}${mo}${d}`;
  }

  nextDay(date) {
    const [y, mo, d] = date.split('-').map(Number);
    const n = new Date(Date.UTC(y, mo - 1, d + 1));
    return `${n.getUTCFullYear()}-${this.pad(n.getUTCMonth() + 1)}-${this.pad(n.getUTCDate())}`;
  }

  // Returns [startStamp, endStamp, isAllDay] for a slot; default duration 2h.
  range(slot) {
    if (!slot.start) return [this.stamp(slot.date), this.stamp(this.nextDay(slot.date)), true];
    let end = slot.end;
    if (!end) end = [Math.min(slot.start[0] + 2, 23), slot.start[1]];
    return [this.stamp(slot.date, slot.start), this.stamp(slot.date, end), false];
  }

  esc(text) {
    return String(text || '')
      .replace(/\\/g, '\\\\')
      .replace(/\r\n|\r|\n/g, '\\n')
      .replace(/[,;]/g, '\\$&');
  }

  // RFC 5545: max. 75 octets per line (UTF-8), continuation lines start with one space.
  // Folds by code point so multi-byte characters are never split.
  fold(line) {
    const encoder = new TextEncoder();
    const out = [];
    let current = '';
    let bytes = 0;
    for (const ch of line) {
      const size = encoder.encode(ch).length;
      if (bytes + size > 75) {
        out.push(current);
        current = ' ';
        bytes = 1;
      }
      current += ch;
      bytes += size;
    }
    out.push(current);
    return out.join('\r\n');
  }

  buildIcs() {
    const now = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d+/, '');
    const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Valldoaido//Events//DE', 'CALSCALE:GREGORIAN'];
    this.slots.forEach((slot, i) => {
      const [start, end, allDay] = this.range(slot);
      const kind = allDay ? ';VALUE=DATE' : '';
      lines.push(
        'BEGIN:VEVENT',
        `UID:${i}-${this.dataset.uid}`,
        `DTSTAMP:${now}`,
        `DTSTART${kind}:${start}`,
        `DTEND${kind}:${end}`,
        `SUMMARY:${this.esc(this.dataset.title)}`
      );
      if (this.dataset.location) lines.push(`LOCATION:${this.esc(this.dataset.location)}`);
      if (this.dataset.description) lines.push(`DESCRIPTION:${this.esc(this.dataset.description)}`);
      lines.push('BEGIN:VALARM', 'TRIGGER:-P1D', 'ACTION:DISPLAY', 'DESCRIPTION:Erinnerung', 'END:VALARM', 'END:VEVENT');
    });
    lines.push('END:VCALENDAR');
    return lines.map((l) => this.fold(l)).join('\r\n');
  }

  downloadIcs() {
    const ics = this.buildIcs();
    const name = (this.dataset.uid || 'event').split('@')[0] + '.ics';
    try {
      const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      window.location.href = 'data:text/calendar;charset=utf-8,' + encodeURIComponent(ics);
    }
  }

  googleUrl() {
    const [start, end] = this.range(this.slots[0]);
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: this.dataset.title || '',
      dates: `${start}/${end}`,
      ctz: 'Europe/Zurich',
      location: this.dataset.location || '',
      details: this.dataset.description || '',
    });
    return 'https://calendar.google.com/calendar/render?' + params.toString();
  }
}

if (!customElements.get('event-add-to-calendar')) {
  customElements.define('event-add-to-calendar', EventAddToCalendar);
}
